'use client';

import { useCallback, useState } from 'react';
import {
  Chip,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ToolButton,
} from '@crabs-ai/ds-react';
import {
  ContentCopy,
  Delete,
  Edit,
  MoreVert,
  Pause,
  PlayArrow,
  VoiceSelection,
} from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { DtoUserVoicePresetType } from '@/shared/constants';
import {
  type VoicePreset,
  type VoicePresetCardProps,
} from '@/entities/voice-preset/model/voice-preset';
import {
  getEmotionLabel,
  getVoiceLabel,
} from '@/entities/voice-preset/lib/labels';

/**
 * 음성 프리셋 카드 — `/my-voice`, TtsDialog '내 목소리' 탭, BrandTemplate 적용 흐름의 공통 카드.
 *
 * 권한 분기는 BE 응답의 canEdit/canDelete/canDuplicate hint 사용 (Phase 1 결정 4 — BE 단일 SoT).
 * 시각: BrandTemplateCard 패턴 답습 — `rounded-sm + border-sys-style-neutral-light`, hover 시 dark border.
 * Don't (DESIGN.md §7) 가드: rounded-full / 퍼플 그라디언트 / shadow-2xl / 컬러 쉐도우 / play emoji 사용 안 함.
 */
export function VoicePresetCard({
  preset,
  mode,
  playingPresetId,
  onPlay,
  onSelect,
  onEdit,
  onDuplicate,
  onDelete,
  onApply,
  isSelected,
  className,
}: VoicePresetCardProps) {
  const t = useTranslations();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isPlaying = playingPresetId === preset.id;
  const showKebab = mode === 'manage';
  const isSystem = preset.type === DtoUserVoicePresetType.system;

  const handleCardClick = useCallback(() => {
    if (isMenuOpen) {
      return;
    }
    if (mode === 'pick') {
      onSelect?.(preset);
      return;
    }
    if (mode === 'apply') {
      onApply?.(preset);
    }
  }, [isMenuOpen, mode, onApply, onSelect, preset]);

  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onPlay(preset);
    },
    [onPlay, preset],
  );

  const handleKebabClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const isInteractive = mode !== 'manage';

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? handleCardClick : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
      className={cn(
        'relative flex flex-col gap-sm rounded-sm border bg-sys-bg-surface p-md transition-all',
        isSelected
          ? 'border-sys-style-primary-default'
          : 'border-sys-style-neutral-light',
        isInteractive &&
          'cursor-pointer hover:border-sys-style-neutral-dark focus-visible:border-sys-style-neutral-dark focus-visible:outline-none',
        className,
      )}
    >
      {/* 우측 상단: 케밥 메뉴 (manage 모드 한정) */}
      {showKebab && (
        <div
          className="absolute top-2xs right-2xs"
          onClick={handleKebabClick}
        >
          <DropdownMenu
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
          >
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('VoicePreset.menu-aria')}
                className={cn(
                  'flex size-lg cursor-pointer items-center justify-center rounded-2xs',
                  'hover:bg-sys-bg-surface-hover active:bg-sys-bg-surface-press',
                  isMenuOpen && 'bg-sys-bg-surface-press',
                )}
              >
                <MoreVert className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={4}
              className="w-max min-w-max"
            >
              {preset.canEdit && onEdit && (
                <DropdownMenuItem
                  className="flex items-center gap-2xs rounded-xs px-xs py-2xs"
                  onSelect={() => onEdit(preset)}
                >
                  <Edit className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
                  <span className="text-body-small text-sys-cont-neutral-default">
                    {t('VoicePreset.edit')}
                  </span>
                </DropdownMenuItem>
              )}
              {preset.canDuplicate && onDuplicate && (
                <DropdownMenuItem
                  className="flex items-center gap-2xs rounded-xs px-xs py-2xs"
                  onSelect={() => onDuplicate(preset)}
                >
                  <ContentCopy className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
                  <span className="text-body-small text-sys-cont-neutral-default">
                    {t('VoicePreset.duplicate')}
                  </span>
                </DropdownMenuItem>
              )}
              {preset.canDelete && onDelete && (
                <DropdownMenuItem
                  className="flex items-center gap-2xs rounded-xs px-xs py-2xs"
                  onSelect={() => onDelete(preset)}
                >
                  <Delete className="size-[20px] [&_path]:fill-sys-cont-negative-default" />
                  <span className="text-body-small text-sys-cont-negative-default">
                    {t('VoicePreset.delete')}
                  </span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* 썸네일 영역 — 음성은 시각이 없어 이름 큰 글자 + 소형 아이콘 + chip 으로 구성 (결정 #4 = C) */}
      <div className="flex flex-col gap-2xs pr-lg">
        <div className="flex items-center gap-2xs">
          <VoiceSelection className="size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-default" />
          <span className="flex-1 truncate text-title-medium text-sys-cont-neutral-default">
            {preset.name}
          </span>
        </div>
        {preset.payload.description && (
          <span className="line-clamp-2 text-caption-medium text-sys-cont-neutral-light">
            {preset.payload.description}
          </span>
        )}
        <span className="text-caption-small text-sys-cont-neutral-lightest">
          {t('VoicePreset.tempo-meta', { value: preset.payload.tempo })}
          {/* pitch 0 = 기본값 (DEFAULT_PITCH), 의미 없는 정보라 표시 skip. */}
          {preset.payload.pitch != null && preset.payload.pitch !== 0
            ? ` · ${t('VoicePreset.pitch-meta', { value: preset.payload.pitch })}`
            : ''}
        </span>
      </div>

      {/* 하단: chip + 영구 PlayArrow/Pause 토글 (결정 #5 = D) */}
      <div className="flex items-center justify-between gap-2xs">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4xs">
          {isSystem && (
            <Chip
              size="sm"
              style="outlined"
              labelNode={t('VoicePreset.system-label')}
              isShowLeadingIcon={false}
              isShowTrailingIcon={false}
            />
          )}
          <Chip
            size="sm"
            style="outlined"
            labelNode={getVoiceLabel(preset.payload.code)}
            isShowLeadingIcon={false}
            isShowTrailingIcon={false}
          />
          {preset.payload.emotionCode && (
            <Chip
              size="sm"
              style="outlined"
              labelNode={getEmotionLabel(preset.payload.emotionCode)}
              isShowLeadingIcon={false}
              isShowTrailingIcon={false}
            />
          )}
        </div>
        <ToolButton
          size="md"
          onClick={handlePlayClick}
          aria-pressed={isPlaying}
          aria-label={
            isPlaying ? t('VoicePreset.pause-aria') : t('VoicePreset.play-aria')
          }
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </ToolButton>
      </div>
    </div>
  );
}

export type { VoicePreset };
